/* eslint-disable */
/**
 * Playwright Automation Script Parser
 * Converts Playwright Python automation scripts into a human-readable visual workflow timeline.
 */

export function humanizeLocator(locator) {
  if (!locator) return "Element";
  
  // Clean parentheses and common wrappers
  let clean = locator.replace(/['"\(\)]/g, "").trim();
  
  // 1. check placeholder pattern
  if (clean.includes('placeholder=')) {
    const match = clean.match(/placeholder=([^,\]\)]+)/i);
    if (match) return `${match[1].trim()} Input Field`;
  }
  
  // 2. check label pattern
  if (clean.includes('label=')) {
    const match = clean.match(/label=([^,\]\)]+)/i);
    if (match) return `${match[1].trim()} Selector`;
  }

  // 3. check name pattern
  if (clean.includes('name=')) {
    const match = clean.match(/name=([^,\]\)]+)/i);
    if (match) {
      const name = match[1].trim();
      return `${name.charAt(0).toUpperCase() + name.slice(1)} Field`;
    }
  }

  // 4. check ID selector style
  if (clean.startsWith('#')) {
    const id = clean.slice(1);
    const spaced = id.replace(/([A-Z])/g, ' $1').trim();
    const cleanId = spaced.charAt(0).toUpperCase() + spaced.slice(1);
    if (cleanId.toLowerCase().includes('btn') || cleanId.toLowerCase().includes('button')) {
      return `${cleanId.replace(/btn|button/i, "").trim()} Button`;
    }
    return `${cleanId} Field`;
  }

  // 5. get_by_role, get_by_placeholder, get_by_text, get_by_label
  if (locator.includes('get_by_role')) {
    const roleMatch = locator.match(/get_by_role\(['"]([^'"]+)['"]/);
    const nameMatch = locator.match(/name=['"]([^'"]+)['"]/);
    if (roleMatch && nameMatch) {
      const roleStr = roleMatch[1].toLowerCase() === 'link' ? 'Link' : 
                      roleMatch[1].toLowerCase() === 'button' ? 'Button' : 
                      roleMatch[1].charAt(0).toUpperCase() + roleMatch[1].slice(1);
      return `${nameMatch[1]} ${roleStr}`;
    }
    if (roleMatch) return `${roleMatch[1].charAt(0).toUpperCase() + roleMatch[1].slice(1)} Element`;
  }

  if (locator.includes('get_by_placeholder')) {
    const m = locator.match(/get_by_placeholder\(['"]([^'"]+)['"]/);
    if (m) return `${m[1]} Input Field`;
  }

  if (locator.includes('get_by_text')) {
    const m = locator.match(/get_by_text\(['"]([^'"]+)['"]/);
    if (m) return `"${m[1]}" Text Label`;
  }

  if (locator.includes('get_by_label')) {
    const m = locator.match(/get_by_label\(['"]([^'"]+)['"]/);
    if (m) return `${m[1]} Field`;
  }

  // Common raw tags
  if (clean.toLowerCase() === 'textarea') return "Description Text Area";
  if (clean.toLowerCase() === 'table') return "Data Table";
  if (clean.includes('[type=password]') || clean.includes('password')) return "Password Field";
  if (clean.includes('[type=submit]') || clean.includes('submit')) return "Submit Button";
  if (clean.includes('[placeholder]')) return "Input Field";

  let pretty = clean.replace(/[\[\]]/g, " ").trim();
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
}

export function parsePlaywrightScript(code) {
  if (!code) return [];

  const lines = code.split('\n');
  const scenarios = [];
  let currentScenario = null;
  let stepIndex = 1;

  // Patterns for scenario boundaries
  const testFuncPattern = /^\s*def\s+(test_\w+)\s*\((.*?)\):/;
  const commentScenarioPattern = /^\s*#\s*Scenario:\s*(.+)$/i;

  // Translation helpers
  const rules = [
    // Navigation
    {
      regex: /page\.goto\(['"]([^'"]+)['"]/,
      type: "navigation",
      icon: "🧭",
      text: (m) => `Navigate to ${m[1]}`,
      executionType: "Browser Navigation"
    },
    {
      regex: /page\.reload\(/,
      type: "navigation",
      icon: "🧭",
      text: () => "Refresh current page",
      executionType: "Browser Action"
    },
    // Inputs / Forms
    {
      regex: /(\.get_by_placeholder|\.get_by_label|\.locator|\.get_by_role)\(([^)]+)\)\.fill\(['"]([^'"]+)['"]\)/,
      type: "input",
      icon: "✍",
      text: (m) => `Enter "${m[3]}" into ${humanizeLocator(m[1] + '(' + m[2] + ')')}`,
      executionType: "Keyboard Input"
    },
    {
      regex: /(\.get_by_placeholder|\.get_by_label|\.locator|\.get_by_role)\(([^)]+)\)\.fill\(CRM_USER\)/,
      type: "input",
      icon: "✍",
      text: (m) => `Enter active Username into ${humanizeLocator(m[1] + '(' + m[2] + ')')}`,
      executionType: "Credential Input"
    },
    {
      regex: /(\.get_by_placeholder|\.get_by_label|\.locator|\.get_by_role)\(([^)]+)\)\.fill\(CRM_PASS\)/,
      type: "input",
      icon: "✍",
      text: (m) => `Enter active Password into ${humanizeLocator(m[1] + '(' + m[2] + ')')}`,
      executionType: "Credential Input"
    },
    {
      regex: /(\.get_by_placeholder|\.get_by_label|\.locator|\.get_by_role)\(([^)]+)\)\.fill\((\w+)\)/,
      type: "input",
      icon: "✍",
      text: (m) => `Enter variable value (${m[3]}) into ${humanizeLocator(m[1] + '(' + m[2] + ')')}`,
      executionType: "Variable Input"
    },
    {
      regex: /(\.get_by_placeholder|\.get_by_label|\.locator|\.get_by_role)\(([^)]+)\)\.select_option\(['"]([^'"]+)['"]\)/,
      type: "input",
      icon: "✍",
      text: (m) => `Select option "${m[3]}" from ${humanizeLocator(m[1] + '(' + m[2] + ')')}`,
      executionType: "Dropdown Selection"
    },
    // Mouse Clicks / Actions
    {
      regex: /(\.get_by_placeholder|\.get_by_label|\.locator|\.get_by_role)\(([^)]+)\)\.click\(\)/,
      type: "mouse",
      icon: "🖱",
      text: (m) => `Click on ${humanizeLocator(m[1] + '(' + m[2] + ')')}`,
      executionType: "Mouse Click"
    },
    {
      regex: /(\.get_by_placeholder|\.get_by_label|\.locator|\.get_by_role)\(([^)]+)\)\.dblclick\(\)/,
      type: "mouse",
      icon: "🖱",
      text: (m) => `Double click on ${humanizeLocator(m[1] + '(' + m[2] + ')')}`,
      executionType: "Mouse Double Click"
    },
    {
      regex: /(\.get_by_placeholder|\.get_by_label|\.locator|\.get_by_role)\(([^)]+)\)\.hover\(\)/,
      type: "mouse",
      icon: "🖱",
      text: (m) => `Hover mouse pointer over ${humanizeLocator(m[1] + '(' + m[2] + ')')}`,
      executionType: "Mouse Hover"
    },
    // Keyboard Key Press
    {
      regex: /page\.keyboard\.press\(['"]([^'"]+)['"]\)/,
      type: "keyboard",
      icon: "⌨",
      text: (m) => `Press keyboard key: ${m[1]}`,
      executionType: "Keyboard Event"
    },
    // Verifications / Assertions
    {
      regex: /expect\(page\)\.to_have_url\((.*?)\)/,
      type: "verification",
      icon: "🛡",
      text: (m) => `Verify current page URL matches target pattern: ${m[1].replace(/re.compile\(|\)/g, "")}`,
      executionType: "URL Verification"
    },
    {
      regex: /expect\((.*?)\)\.to_have_value\((.*?)\)/,
      type: "verification",
      icon: "🛡",
      text: (m) => `Verify ${humanizeLocator(m[1])} has correct value`,
      executionType: "Value Assertion"
    },
    {
      regex: /expect\((.*?)\)\.to_be_visible\(/,
      type: "verification",
      icon: "🛡",
      text: (m) => `Verify that ${humanizeLocator(m[1])} is visible on screen`,
      executionType: "Visibility Assertion"
    },
    {
      regex: /assert\s+(.+)/,
      type: "verification",
      icon: "🛡",
      text: (m) => `Assert expression matches: ${m[1]}`,
      executionType: "Condition Assert"
    },
    // Waiting states
    {
      regex: /page\.wait_for_selector\(['"]([^'"]+)['"]\s*,\s*state=['"]visible['"]/,
      type: "waiting",
      icon: "⏱",
      text: (m) => `Wait until element ${humanizeLocator(m[1])} becomes visible`,
      executionType: "Selector Synchronization"
    },
    {
      regex: /page\.wait_for_load_state\(['"]([^'"]+)['"]\)/,
      type: "waiting",
      icon: "⏱",
      text: (m) => `Wait for page load state: "${m[1]}"`,
      executionType: "Page Synchronization"
    },
    {
      regex: /time\.sleep\((\d+)\)/,
      type: "waiting",
      icon: "⏱",
      text: (m) => `Pause execution for ${m[1]} seconds`,
      executionType: "Explicit Wait"
    },
    // Browser states
    {
      regex: /sync_playwright\(\)/,
      type: "browser",
      icon: "🌐",
      text: () => "Launch automation browser instance",
      executionType: "Context Creation"
    },
    {
      regex: /browser\.close\(\)/,
      type: "browser",
      icon: "🌐",
      text: () => "Close browser instance",
      executionType: "Context Destruction"
    },
    // Screenshot
    {
      regex: /page\.screenshot\(path=/,
      type: "screenshot",
      icon: "📸",
      text: () => "Capture execution screenshot",
      executionType: "Visual Capture"
    }
  ];

  for (let line of lines) {
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) continue;

    // Detect comment scenario boundary
    const commentMatch = trimmed.match(commentScenarioPattern);
    if (commentMatch) {
      if (currentScenario) {
        scenarios.push(currentScenario);
      }
      currentScenario = {
        name: commentMatch[1].trim(),
        steps: []
      };
      stepIndex = 1;
      continue;
    }

    // Detect test function boundary
    const testFuncMatch = trimmed.match(testFuncPattern);
    if (testFuncMatch) {
      if (currentScenario) {
        scenarios.push(currentScenario);
      }
      const rawName = testFuncMatch[1].replace('test_', '').replace(/_/g, ' ');
      const friendlyName = rawName.charAt(0).toUpperCase() + rawName.slice(1);
      currentScenario = {
        name: friendlyName,
        steps: []
      };
      stepIndex = 1;
      continue;
    }

    // If we have an active scenario, match lines inside it
    if (currentScenario) {
      // Skip import statements, simple assignments or wrapper boundaries
      if (trimmed.startsWith('import ') || trimmed.startsWith('from ') || trimmed.startsWith('def ') || trimmed.startsWith('CRM_USER') || trimmed.startsWith('CRM_PASS') || trimmed.startsWith('unique_suffix') || trimmed.startsWith('video_dir') || trimmed.startsWith('screenshot_dir') || trimmed.startsWith('os.makedirs') || trimmed.startsWith('elapsed =') || trimmed.startsWith('print(') || trimmed === 'try:' || trimmed.startsWith('except ') || trimmed.startsWith('with ') || trimmed === 'pass' || trimmed === 'return' || trimmed.startsWith('class ')) {
        continue;
      }

      // Try matching translation rules
      let matchedStep = null;
      for (const rule of rules) {
        const match = trimmed.match(rule.regex);
        if (match) {
          matchedStep = {
            stepNumber: stepIndex++,
            actionType: rule.type,
            actionIcon: rule.icon,
            friendlyDescription: rule.text(match),
            originalLocator: trimmed,
            statusIndicator: "passed",
            executionType: rule.executionType
          };
          break;
        }
      }

      // If no regex match but is some page operation, generic fallback step
      if (!matchedStep && (trimmed.includes('page.') || trimmed.includes('expect(') || trimmed.includes('assert '))) {
        matchedStep = {
          stepNumber: stepIndex++,
          actionType: trimmed.includes('expect') || trimmed.includes('assert') ? "verification" : "browser",
          actionIcon: trimmed.includes('expect') || trimmed.includes('assert') ? "🛡" : "🌐",
          friendlyDescription: trimmed.length > 50 ? trimmed.substring(0, 47) + "..." : trimmed,
          originalLocator: trimmed,
          statusIndicator: "passed",
          executionType: "Standard Step"
        };
      }

      if (matchedStep) {
        currentScenario.steps.push(matchedStep);
      }
    }
  }

  if (currentScenario) {
    scenarios.push(currentScenario);
  }

  return scenarios;
}
